import { DateTime } from 'luxon';
import { ParkerRegistrationOffense } from 'src/app/base/models/models.swagger.generated';
import { ParkingSessionTimeSlice } from './parking-session-time-slice';
import { ParkingSessionTimeSliceCollection } from './parking-session-time-slice-collection';
import { ParkingTariffValidity } from './parking-tariff-validity';
import { ParkingTimeGrid } from './parking-time-grid';
import { TariffSelectionStrategy } from './tariff-selection-strategies';

// copied form backend
export class SessionTimeSlicer
{
  public activeTariffValidity: ParkingTariffValidity;
  public activeTimeSlice: ParkingSessionTimeSlice;
  public timeGrid: ParkingTimeGrid;

  private constructor(timeGrid: ParkingTimeGrid)
  {
      this.timeGrid = timeGrid;
  }

  public static for(timeGrid: ParkingTimeGrid): SessionTimeSlicer
  {
      return new SessionTimeSlicer(timeGrid);
  }

  public sliceOverWholeGridSpan(): ParkingSessionTimeSliceCollection
  {
      return this.slice(this.timeGrid.start, this.timeGrid.end);
  }

  public slice(startTime: DateTime, endTime: DateTime): ParkingSessionTimeSliceCollection
  {
      const timeSliceCollection = new ParkingSessionTimeSliceCollection();
      timeSliceCollection.push(...this.sliceRecursively(startTime, endTime));
      return timeSliceCollection;
  }

  private sliceRecursively(startTime: DateTime, sessionEndTime: DateTime, activeTariffValidity?: ParkingTariffValidity,
                            activeTariffRunningSince?: DateTime): ParkingSessionTimeSlice[]
  {
      const parkingSessionTimeSlices: ParkingSessionTimeSlice[] = [];
      this.activeTimeSlice = new ParkingSessionTimeSlice(startTime);
      parkingSessionTimeSlices.push(this.activeTimeSlice);

      const tariffValidityList = this.timeGrid.getTariffValiditiesAt(startTime, activeTariffValidity?.tariff, activeTariffRunningSince);
      if (!tariffValidityList?.length) // no tariffs available at this point. This is an offense
      {
          //if no tariff is active at the moment, then it is during entry. hence zone not allowed, else parking time too long
          this.activeTimeSlice.offense = activeTariffValidity != null ? ParkerRegistrationOffense.ParkingTimeTooLong : ParkerRegistrationOffense.ZoneNotAllowed;
          this.activeTimeSlice.timeRange.endTime = sessionEndTime;
      }
      else
      {
          // select next tariff. Currently only the first next available tariff
          const tariffSelectionStrategy = TariffSelectionStrategy.Factory(tariffValidityList);
          this.activeTariffValidity = tariffSelectionStrategy.selectNext(startTime, activeTariffValidity);
          if (this.activeTariffValidity == null)
          {
              this.activeTimeSlice.offense = activeTariffValidity != null ? ParkerRegistrationOffense.ParkingTimeTooLong : ParkerRegistrationOffense.ZoneNotAllowed;
              this.activeTimeSlice.timeRange.endTime = sessionEndTime;
          }
          else
          {
              this.activeTimeSlice.timeRange.endTime = this.evaluateNextTariffSwitchOverTime(startTime, sessionEndTime);
              this.activeTimeSlice.tariff = this.activeTariffValidity.tariff;
              const nextSlicer = new SessionTimeSlicer(this.timeGrid);
              if (this.activeTimeSlice.timeRange.endTime < sessionEndTime)
              {
                  parkingSessionTimeSlices.push(...nextSlicer.sliceRecursively(this.activeTimeSlice.timeRange.endTime,
                      sessionEndTime, this.activeTariffValidity, startTime));
              }
          }
      }
      return parkingSessionTimeSlices;
  }

  private evaluateNextTariffSwitchOverTime(startTime: DateTime, sessionEndTime: DateTime): DateTime
  {
    const tariffEndTime = this.activeTariffValidity.endDateTime;

    let tariffEndTimeMaxDuration = tariffEndTime;
    if (this.activeTariffValidity.tariff.maxParkingDurationInMin)
    {
      // tariff's end time
      tariffEndTimeMaxDuration = startTime.plus({minutes: this.activeTariffValidity.tariff.maxParkingDurationInMin});
    }

    let foreignParkingTariffNextSwitchOverTime = tariffEndTime;
    if (this.activeTariffValidity.isFallbackTariff)
    {
      foreignParkingTariffNextSwitchOverTime = this.timeGrid.getNextSwitchoverTimeForForeignParkingTariff(startTime, sessionEndTime);
    }

    return DateTime.min(tariffEndTime, tariffEndTimeMaxDuration, sessionEndTime, foreignParkingTariffNextSwitchOverTime);
  }
}
